import store from '@/store/store';
import { isArray, hasProp } from '@/utils/validate.utils';
import { parseString, parseNumber } from '@/utils/sanitaze.utils';
import { consoleError } from '@/utils/console.utils';
import { validateDevice } from '@/models/devices.model';
import { validateSchedule } from '@/models/schedules.model';
import { validateScene } from '@/models/scenes.model';
import { USER_TYPE } from '@/app.config';

/**
 * @typedef {Object} Installation
 * @property {String} name - Nombre de la instalación
 * @property {String} timezone - Timezone donde se encuentra la instalación
 * @property {String} type - Tipo del usuario en la instalación ('basic', 'advanced')
 * @property {Number} units - Unidad de la temperatura (F, C)
 * @property {Object} devices - Dispositivos de una instalación
 * @property {Number} lat - Latitud de una instalación
 * @property {Number} long - Longitud de una instalación
 */

/**
 * Da formato a los datos de una instalación
 *
 * @param {String} installationID - Id de la instalación
 * @param {Object} installationData - Datos de una instalación del backend
 * @return {Installation} - Datos formateados de una instalación
 */
// eslint-disable-next-line consistent-return
const sanitizeInstallation = (installationID, installationData) => {
  try {
    const installation = {};

    if (hasProp(installationData, 'name')) {
      installation.name = parseString(installationData.name);
    }
    if (hasProp(installationData, 'timezoneId')) {
      installation.timezone = parseString(installationData.timezoneId);
    }
    if (hasProp(installationData, 'type')) {
      installation.type =
        installationData.type === USER_TYPE.ADVANCED ? USER_TYPE.ADVANCED : USER_TYPE.BASIC;
    }
    if (hasProp(installationData, 'units')) {
      installation.units = parseNumber(installationData.units);
    }
    if (hasProp(installationData, 'coords') && hasProp(installationData.coords, 'lat')) {
      installation.lat = parseNumber(installationData.coords.lat);
    }
    if (hasProp(installationData, 'coords') && hasProp(installationData.coords, 'lng')) {
      installation.long = parseNumber(installationData.coords.lng);
    }

    return installation;
  } catch (error) {
    consoleError(`Error al parsear los datos de la instalación ${installationID}`);
    store.dispatch('globalModule/setError', 'invalidInstallation');
    return null;
  }
};

/**
 * Comprueba si los datos de una instalación son válidos cuando se recibe por primera vez
 *
 * @param {Object} installationData - Datos de la instalación del backend
 * @return {Installation} - Datos formateados y validados de una instalación
 */
// eslint-disable-next-line consistent-return
const validateInstallation = installationData => {
  const installation = sanitizeInstallation(installationData._id, installationData);

  /**
   * Compruebo que tiene los datos mínimos
   */
  if (
    !installation ||
    !installation.name ||
    !installation.timezone ||
    !installation.type ||
    (installation.units !== 0 && installation.units !== 1)
  ) {
    consoleError(`Error al validar los datos de la instalación ${installationData._id}`);
    store.dispatch('globalModule/setError', 'invalidInstallation');
    return null;
  }

  /**
   * Valido y añado los dispositivos de la instalación
   */
  installation.devices = {};
  if (hasProp(installationData, 'devices') && isArray(installationData.devices)) {
    installationData.devices.forEach(device => {
      const deviceParse = validateDevice(device);

      if (deviceParse) {
        installation.devices[device.mac] = deviceParse;
      }
    });
  }

  /**
   * Valido y añado las programaciones de la instalación
   */
  if (hasProp(installationData, 'schedules') && isArray(installationData.schedules)) {
    installation.schedules = {};
    installationData.schedules.forEach(schedule => {
      if (!schedule) return;

      const scheduleParse = validateSchedule(schedule);

      if (scheduleParse) {
        installation.schedules[scheduleParse.id] = scheduleParse;
      }
    });
  }

  /**
   * Valido y añado las escenas de la instalación
   */
  if (hasProp(installationData, 'scenes') && isArray(installationData.scenes)) {
    installation.scenes = {};
    installation.scenesOrder = [];
    installationData.scenes.forEach(scene => {
      if (!scene) return;

      const sceneParse = validateScene(scene);

      if (sceneParse) {
        installation.scenes[sceneParse.id] = sceneParse;
        installation.scenesOrder.push(sceneParse.id);
      }
    });
  }

  return installation;
};

/**
 * Valida y da formato a los datos de varias instalaciones.
 * Devuelve un objeto donde cada propiedad representa una instalación
 * siendo el key el id de la instalación
 *
 * @param {Array} installations - Listado de instalaciones
 * @return {Object<string, Installation>} - Objeto con las instalaciones parseadas
 */
const validateInstallations = installations => {
  if (!isArray(installations)) {
    store.dispatch('globalModule/setError', 'invalidInstallations');
    return null;
  }

  // Build custom data model
  const parseInstallation = {};

  installations.forEach(installation => {
    const installationParse = validateInstallation(installation);

    if (installationParse) {
      parseInstallation[installation._id] = installationParse;
    }
  });

  return parseInstallation;
};

export { sanitizeInstallation, validateInstallation, validateInstallations };
